import React, { useState, useEffect, useCallback, useMemo } from "react";
import { generateColorPalette } from "../../utils/generateColorPalette";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Bar } from 'react-chartjs-2';
import { Card, CardContent, CardHeader, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import moment from "moment";
import { differenceInCalendarDays } from 'date-fns';
import { useDataFetcher } from "../../hooks/useDataFetcher";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export const ServiceCallTimes = ({ dateRanges }) => {
  const [jobStats, setJobStats] = useState([]);
  const [chartData, setChartData] = useState(null);

  const startTime = useMemo(() => moment(dateRanges[0]), [dateRanges]);
  const endTime = useMemo(() => moment(dateRanges[1]), [dateRanges]);

  // Use centralized data fetcher for service calls over time
  const { data: overTimeData, loading: overTimeLoading } = useDataFetcher(
    `service_calls_over_time?s=${startTime.unix()}&e=${endTime.unix()}`,
    {},
    { interval: 300000 } // 5 minutes
  );

  // Use centralized data fetcher for time to complete calls
  const { data: timeToCompleteData, loading: timeToCompleteLoading } = useDataFetcher(
    `time_to_complete_calls?s=${startTime.unix()}&e=${endTime.unix()}`,
    {},
    { interval: 300000 } // 5 minutes
  );

  const formatSecondsToTime = (seconds) => {
    let date = new Date(0);
    date.setSeconds(seconds);
    return date.toISOString().substr(11, 8);
  };

  // Process chart data when overTimeData changes
  useEffect(() => {
    if (overTimeData && overTimeData.length > 0) {
      console.log("call times result", overTimeData);
      // Determine grouping granularity
      const days = differenceInCalendarDays(new Date(dateRanges[1]), new Date(dateRanges[0]));
      const groupByMonth = days > 31;
      const getGroupKey = (dateStr) => {
        const date = new Date(dateStr);
        return groupByMonth
          ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
          : date.toISOString().split('T')[0];
      };
      // Aggregate data
      const aggregated = {};
      overTimeData.forEach(item => {
        const groupKey = getGroupKey(item.call_date);
        const type = item.service_call_type;
        if (!aggregated[groupKey]) aggregated[groupKey] = {};
        if (!aggregated[groupKey][type]) aggregated[groupKey][type] = 0;
        aggregated[groupKey][type] += Number(item.total_duration_seconds);
      });
      // Get all unique service call types
      const allTypes = Array.from(new Set(overTimeData.map(item => item.service_call_type)));
      // Build datasets
      const labels = Object.keys(aggregated).sort();
      const datasets = allTypes.map((type, idx) => ({
        label: type,
        data: labels.map(label => aggregated[label][type] || 0),
        backgroundColor: generateColorPalette(allTypes.length)[idx],
      }));
      setChartData({
        labels,
        datasets
      });
    }
  }, [overTimeData, dateRanges]);

  // Process job stats when both data sources are available
  useEffect(() => {
    if (timeToCompleteData && timeToCompleteData.length > 0 && overTimeData && overTimeData.length > 0) {
      console.log('time_to_complete_calls API response:', timeToCompleteData);

      timeToCompleteData.sort((a, b) => parseInt(a.average_completion_time_seconds) - parseInt(b.average_completion_time_seconds));
      // Aggregate total calls from overTimeData
      const totalCallsByType = {};
      overTimeData.forEach(item => {
        const type = item.service_call_type;
        totalCallsByType[type] = (totalCallsByType[type] || 0) + Number(item.total_calls);
      });
      const stats = timeToCompleteData.map(item => ({
        label: item.normalized_service_call_type,
        value: formatSecondsToTime(item.average_completion_time_seconds),
        totalCalls: totalCallsByType[item.normalized_service_call_type] || 0
      }));
      console.log('Mapped jobStats for table:', stats);
      setJobStats(stats);
    }
  }, [timeToCompleteData, overTimeData]);

  const normalizeServiceCallType = (type) => {
    const lowercaseType = type.toLowerCase();
    if (['preventative maintenance', 'preventitive maintenance', 'preventive maintenance'].includes(lowercaseType)) {
      return 'Preventative Maintenance';
    }
    return type;
  };

  const options = {
    scales: {
      x: {
        stacked: true,
        type: 'time',
        time: {
          unit: (() => {
            if (dateRanges && dateRanges.length === 2) {
              const days = differenceInCalendarDays(new Date(dateRanges[1]), new Date(dateRanges[0]));
              return days > 31 ? 'month' : 'day';
            }
            return 'day';
          })()
        }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          callback: (value) => formatSecondsToTime(value), // Format y-axis ticks to hh:mm:ss
        }
      }
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function(context) {
            // Here we're using the label callback instead of afterLabel to format the values properly
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatSecondsToTime(context.parsed.y); // Format the y value
            }
            return label;
          }
        }
      }
    }
  };

  if (overTimeLoading || timeToCompleteLoading || !chartData || jobStats.length === 0) {
    return <div>Loading...</div>;
  }

  return (
    <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
      <Grid item xs={12} lg={4}>
        <TableContainer component={Paper}>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell>Service Call Type</TableCell>
                <TableCell align="right">AVG Time</TableCell>
                <TableCell align="right">Total Calls</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobStats.map(({ label, value, totalCalls }, index) => (
                <TableRow
                  key={`service-call-${index}`}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell>{label}</TableCell>
                  <TableCell align="right">{value}</TableCell>
                  <TableCell align="right">{totalCalls}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item xs={12} lg={8}>
        <Card>
          <CardHeader title="Service Call Times" />
          <CardContent>
            <Bar data={chartData} options={options} />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}